import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";

/**
 * Interface representing a Category in the autocomplete dropdown
 * Defines the structure of each Category option
 *
 * @property id - Unique identifier for the Category
 * @property title - Category number/identifier string
 */
interface CategoryOption {
    id: number;
    title: string;
}

/**
 * Props interface for the CategoryAutocomplete component
 * Extends the base autocomplete functionality with Category-specific features
 *
 * @property industryId - Optional industry filter for Category search
 * @property textFieldProps - Additional props to pass to the TextField component
 * @property form - Whether the component is used within a form
 * @property name - Name attribute for form integration
 * @property required - Whether the field is required in forms
 * @property setLoadingOptions - Callback to handle loading state changes
 * @property value - Currently selected Category option
 * @property onChange - Callback fired when selection changes
 */
interface CategoryAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<CategoryOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    industryId?: string;
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    onlyRoot?: boolean;

    [key: string]: any;
}

/**
 * Interface for the Category search API response
 * Defines the structure of the search response data
 *
 * @property data - Array of Category results
 * @property data[].id - Unique identifier for each Category
 * @property data[].CategoryNo - Category number/identifier string
 */
interface CategorySearchResponse {
    data?: Array<CategoryOption & { parentId?: number }>;
}

/**
 * CategoryAutocomplete Component
 *
 * A searchable dropdown component for selecting Categorys with the following features:
 * - Industry-specific Category filtering
 * - Async option loading with debouncing
 * - Loading state management
 * - Form integration support
 * - Customizable styling and behavior
 *
 * The component uses the autoCompleteCategoryLookup API to fetch Categorys based on:
 * - Search keywords (Category number matching)
 * - Industry ID (optional filter)
 *
 * @component
 */
const CategoryAutocomplete = <M extends boolean = false, >({
                                                               industryId = undefined,
                                                               textFieldProps = {},
                                                               form = false,
                                                               name = undefined,
                                                               required = false,
                                                               onlyRoot = false,
                                                               setLoadingOptions: setLoadingOptionsProps,
                                                               ...props
                                                           }: CategoryAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches Category options based on search keywords and industry
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string (matches against Category numbers)
     * @returns Array of matching Category options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken) => {
        if (!industryId)
            return [];
        setLoadingOptions(true);
        try {
            // @ts-ignore - API type definition is not complete
            const response = await Api.getAllPartCategoriesNew(industryId, cancelToken as any) as CategorySearchResponse;
            if (!isMounted())
                return [];
            return response?.data
                ?.filter(e => !onlyRoot || !e.parentId)
                ?.map(e => ({
                    id: e.id,
                    title: e.title,
                })) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [industryId, isMounted, onlyRoot]);

    /**
     * Determines if two Category options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: CategoryOption, value: CategoryOption | null) =>
        option.id === value?.id, []);

    /**
     * Gets the display label for a Category option (Category number)
     */
    const getOptionLabel = useCallback((option: CategoryOption) => option?.title ?? "", []);

    /**
     * Gets the unique key for a Category option
     */
    const getOptionKey = useCallback((option: CategoryOption) => option?.id ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<CategoryOption, M>
            keywordsBased={false}
            getOptions={getOptions}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default CategoryAutocomplete;